import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useSession } from '../hooks/session';
import { Badge, Card, List, Row, ScreenTitle, Section } from '../components/ui';
import { ROLE_LABEL } from '../lib/permissions';

/**
 * Указатель по настройкам.
 *
 * Раньше здесь лежало всё сразу: тон ответов, автопубликация, портрет бренда и
 * десяток тумблеров уведомлений — экран приходилось листать, чтобы найти что
 * угодно. Теперь это короткий список разделов, а формы живут внутри них.
 */
export function SettingsPage() {
  const navigate = useNavigate();
  const session = useSession();

  const org = useQuery({ queryKey: ['organization'], queryFn: api.organization });
  const points = useQuery({ queryKey: ['points'], queryFn: api.points });
  const prefs = useQuery({ queryKey: ['notifications'], queryFn: api.notifications });

  const settings = org.data?.settings;
  const pointCount = points.data?.points.length ?? 0;

  const notificationsSummary = (() => {
    if (!prefs.data) return 'Что присылать в Telegram';
    if (prefs.data.newReviewAll) return 'Все новые отзывы';
    if (prefs.data.newReviewNegativeOnly) return 'Только негативные';
    return 'Только сводки';
  })();

  return (
    <div className="mx-auto max-w-lg pb-8">
      <ScreenTitle title="Настройки" />

      <Section title="Вы">
        {/* Человек должен понимать, кто он в системе: иначе непонятно,
            почему часть настроек только для чтения. */}
        <Card className="mx-4 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="t-body font-medium">{session.userName}</p>
            <p className="t-footnote text-[var(--color-hint)]">{org.data?.name ?? 'Организация'}</p>
          </div>
          <Badge color={session.isDeveloper ? 'var(--color-accent)' : undefined}>
            {ROLE_LABEL[session.role ?? ''] ?? 'Без роли'}
          </Badge>
        </Card>

        {session.organizations.length > 1 && (
          <Card className="mx-4 mt-2">
            <label className="t-footnote mb-1.5 block text-[var(--color-hint)]">Активная организация</label>
            <select
              value={session.activeOrganizationId ?? ''}
              onChange={(event) => session.selectOrganization(event.target.value)}
              className="t-body min-h-[46px] w-full rounded-[var(--radius-control)] bg-[var(--color-surface)] px-3 text-[var(--color-text)]"
            >
              {session.organizations.map((organization) => (
                <option key={organization.id} value={organization.id}>
                  {organization.name}
                </option>
              ))}
            </select>
          </Card>
        )}
      </Section>

      <Section title="Работа с отзывами">
        <List className="mx-4">
          <Row
            title="Ответы"
            subtitle={
              settings
                ? settings.autoReplyEnabled
                  ? 'Публикуются автоматически'
                  : 'Нужно подтверждение'
                : 'Тон, подпись и автопубликация'
            }
            chevron
            onClick={() => navigate('/settings/replies')}
          />
          <Row
            title="Точки и площадки"
            subtitle={pointCount > 0 ? `${pointCount} точек` : '2ГИС и Яндекс Карты'}
            chevron
            onClick={() => navigate('/settings/accounts')}
          />
          <Row
            title="Уведомления"
            subtitle={notificationsSummary}
            chevron
            onClick={() => navigate('/settings/notifications')}
          />
        </List>
      </Section>

      <Section title="Организация">
        <List className="mx-4">
          <Row title="Сотрудники" subtitle="Доступ и роли" chevron onClick={() => navigate('/settings/team')} />
          {session.can.viewBilling && (
            <Row
              title="Оплата и расходы"
              subtitle="Сервер и токены ИИ"
              chevron
              onClick={() => navigate('/settings/billing')}
            />
          )}
        </List>
      </Section>
    </div>
  );
}
